<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight"
    :embedded="embedded">
    <div class="row no-gutters h-100">
      <div class="col-2 p-2">
        <AnalyticsDashboardValueRow>
          <div class="col">
            <ReportFilterPopover
              headerTitle="Monthly Payment Value Filter"
              :showApplyButton="true"
              :showResetButton="true"
              :appendToBody="true"
              alignment="left"
              placement="bottom-end"
              @apply="applyPaymentValueFilter"
              @reset="resetPaymentValueFilter">
              <template v-slot:popover>
                <ReportDateRangeSelect 
                  :startDate="editingPaymentValueFilter.startDate"
                  :endDate="editingPaymentValueFilter.endDate"
                  @updateStartDate="editingPaymentValueFilter.startDate = $event"
                  @updateEndDate="editingPaymentValueFilter.endDate = $event"
                />
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedPaymentValueFilter.startDate | formatDate}} - {{appliedPaymentValueFilter.endDate | formatDate}}
                </div>                
              </template>
            </ReportFilterPopover>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Total Payment Amount" highlight>
              {{formatCurrency(paymentSummaryData.paymentTotalValue)}}
              <template v-slot:info>Over {{paymentSummaryData.totalPaymentCount}} payment{{displayCountPlural(paymentSummaryData.totalPaymentCount)}} during the period</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Average Monthly Amount">
              {{formatCurrency(paymentSummaryData.averagePaymentsValue)}}
              <template v-slot:info>Over {{paymentSummaryData.totalPaymentCount}} payment{{displayCountPlural(paymentSummaryData.totalPaymentCount)}} during the period</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
        <AnalyticsDashboardValueRow v-if="paymentSummaryData.largestPaymentInfo">
          <div class="col">
            <AnalyticsDashboardValue title="Largest Payment">
              {{formatCurrency(paymentSummaryData.largestPaymentInfo.amount)}}
              <template v-slot:info>Payment <LinkPaymentReceived :id="paymentSummaryData.largestPaymentInfo.id" :name="paymentSummaryData.largestPaymentInfo.sequenceNumber"></LinkPaymentReceived> for {{paymentSummaryData.largestPaymentInfo.contact.name}} on {{formatDate(paymentSummaryData.largestPaymentInfo.datePayment)}}</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
      </div>
      <div class="col-10">
        <ChartBar
          title="Monthly Payments Received"
          :series="paymentMonthlyValueData.series"
          :xAxis="paymentMonthlyValueData.xAxis"
          :yAxis="paymentMonthlyValueData.yAxis"
          :legend="paymentMonthlyValueData.legend"
          :options="paymentMonthlyValueData.options"
          :popupEnabled="true"
          :exportEnabled="true"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'

import Utils from '@/utils/utils'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardInfo from '@/modules/intelligence/components/analyticsDashboardInfo'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

import ChartBar from '@/components/charts/chartBar'

export default {
  name: 'PaymentMonthlyChartWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValueRow,
    AnalyticsDashboardValue,
    AnalyticsDashboardInfo,
    ChartBar,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    filter: Object,
    contactId: Number,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean
  },
  data() {
    return {
      paymentSummaryData: {
        totalPaymentAmount: 0,
        totalPaymentCount: 0,
        averagePaymentsValue: 0,
        largestPaymentInfo: null,
      },
      paymentMonthlyValueData: {
        series: [],
        xAxis: {},
        yAxis: {},
        legend: {},
        options: {}
      },
      defaultPaymentValueFilter: {    
        startDate: moment().local().subtract(7, 'years').startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString()
      },
      editingPaymentValueFilter: {},
      appliedPaymentValueFilter: {},
    }
  },
  computed: {
    hasContactFilter() {
      return this.contactId != null
    }
  },
  mounted() {
    this.editingPaymentValueFilter = _.clone(this.defaultPaymentValueFilter)
    this.appliedPaymentValueFilter = _.clone(this.defaultPaymentValueFilter)

    this.requestChartData()
  },
  beforeDestroy() {
  },
  watch: {
    contactId: function() {
      this.requestChartData()
    },
    filter: function() {
      this.requestChartData()
    }
  },
  methods: {
    applyPaymentValueFilter() {
      this.appliedPaymentValueFilter = _.clone(this.editingPaymentValueFilter)

      this.requestChartData()
    },
    resetPaymentValueFilter() {
      this.editingPaymentValueFilter = _.clone(this.defaultPaymentValueFilter)
      this.appliedPaymentValueFilter = _.clone(this.defaultPaymentValueFilter)

      this.requestChartData()
    },
    requestChartData() {
      var paymentValueFilter = {
        type: 'monthly',
        startDate: this.appliedPaymentValueFilter.startDate,
        endDate: this.appliedPaymentValueFilter.endDate,
        contactId: this.contactId,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null
      }

      Promise.all([
        this.$apiService.analyticsService.getPaymentsValueChartData(paymentValueFilter),
        this.$apiService.analyticsService.getPaymentsSummary(paymentValueFilter)
      ])
      .then(results => {
        let chartData = results[0].data
        let paymentSummaryData = results[1].data

        let monthList = []
        let seriesList = []
        let yearList = []
        for(let i=0; i<12; i++)
          monthList.push(moment().month(i).format('MMM'))

        // Format scattered data into chart data
        if(chartData.length > 0) {
          let firstYear = chartData[0].year
          let lastYear = moment().local().year()

          for(let year=firstYear; year<=lastYear; year++) {
            yearList.push(`${year}`)

            let yearData = chartData.filter(paymentInfo => paymentInfo.year == year)
            let yearValueSeries = monthList.map((m, monthIndex) => {
              let monthInfo = yearData.find(paymentInfo => paymentInfo.month == monthIndex + 1)   // Note: Month is one based (ie, 1-12)
              return monthInfo ? monthInfo.totalAmount : 0
            })

            seriesList.push({
              name: `${year}`,
              type: 'bar',
              data: yearValueSeries,
              markLine: {
                label: {
                  formatter: (param) => {
                    return this.formatCurrency(param.value);
                  }
                },
                data: [
                  {
                    type: 'average',
                    name: 'Avg',
                  }
                ]
              },
              tooltip: {
                valueFormatter: (value) => {
                  return this.formatCurrency(value);
                }
              }
            })
          }
        }

        this.paymentMonthlyValueData.legend = {
          data: yearList
        }
        this.paymentMonthlyValueData.xAxis = {
          type: 'category',
          data: monthList
        }
        this.paymentMonthlyValueData.yAxis = [
          {
            type: 'value',
            axisLabel: {
              formatter: (value) => {
                return this.formatCurrencySuffix(value, 1);
              }
            }
          }
        ]

        this.paymentMonthlyValueData.series = seriesList

        // Summary info
        this.paymentSummaryData.averagePaymentsValue = 0
        if(chartData.length > 0) {
          this.paymentSummaryData.averagePaymentsValue = chartData.reduce((curValue, invoiceInfo) => curValue + invoiceInfo.totalAmount, 0) / chartData.length
        }

        this.paymentSummaryData.totalPaymentAmount = paymentSummaryData.totalPaymentAmount
        this.paymentSummaryData.totalPaymentCount = paymentSummaryData.totalPaymentCount
        this.paymentSummaryData.largestPaymentInfo = paymentSummaryData.largestPayment        
      })
    },
  }
}
</script>